/* eslint-disable complexity */
import SellIcon from '@mui/icons-material/Sell'
import {
  Chip,
  Paper,
  Stack,
  styled,
  Tooltip,
  tooltipClasses,
  TooltipProps,
  Typography
} from '@mui/material'
import dayjs from 'dayjs'

import { SerializableAppointmentEntry } from '../../models/SerializableSdkTypes/SerializableAppointmentEntry'
import {
  CustomerEntry,
  RepairOrderExpanded,
  RepairOrderStatusEntry,
  UserProfile,
  VehicleEntry
} from '../../sdk'
import { describeLicense, describeVehicle } from '../../utils'
import { formatName } from '../../utils/formatCustomerName/formatCustomerName'
import { localDateTimeArrayToDate } from '../../utils/localDateTimeArraytoDate/localDateTimeArrayToDate'
import { RepairOrderStatusChip } from '../SharedUI/RepairOrderStatusChip'
import { ROLink } from '../SharedUI/ROLink'
import {
  APPOINTMENT_COLORS_DARK,
  AppointmentColorSelect
} from './AppointmentColorSelect'
const containerProps = {
  height: '100%',
  overflow: 'hidden',
  p: '2px'
}
const CalenderTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: 'white',
    borderRadius: 0,
    padding: 0,
    margin: 0
  },
  [`& .${tooltipClasses.tooltipPlacementBottom}`]: {
    marginTop: '0 !important',
    marginBottom: '0 !important'
  },
  [`& .${tooltipClasses.tooltipPlacementTop}`]: {
    marginTop: '0 !important',
    marginBottom: '0 !important'
  },
  [`& .${tooltipClasses.tooltipPlacementLeft}`]: {
    marginRight: '0 !important'
  },
  [`& .${tooltipClasses.tooltipPlacementRight}`]: {
    marginLeft: '0 !important'
  }
}))

export const renderEventBlock = (
  exAppt: SerializableAppointmentEntry,
  customer: CustomerEntry | undefined,
  vehicle: VehicleEntry | undefined,
  repairOrder: RepairOrderExpanded | undefined,
  statuses: RepairOrderStatusEntry[] | undefined,
  roToTechniciansMap: { [key: string]: UserProfile[] },
  calendar: 'appt' | 'resource',
  view: 'week' | 'day',
  selected: boolean,
  setColor: (color: string, app: SerializableAppointmentEntry) => void
) => {
  const color =
    exAppt.info!.color ||
    (exAppt.info!.waiting
      ? APPOINTMENT_COLORS_DARK[1]
      : APPOINTMENT_COLORS_DARK[0])
  const elevation = selected ? 4 : 0

  const customerName = formatName(customer?.information)

  const vehicleYMM = vehicle ? describeVehicle(vehicle.info!) : ''
  const vehicleLicense =
    vehicle && vehicle.info!.licensePlate ? describeLicense(vehicle.info!) : ''
  const technicians = repairOrder && roToTechniciansMap[repairOrder.entry!.id!]

  const isConfirmed = exAppt.info.isConfirmed

  return (
    <CalenderTooltip
      title={
        <Paper elevation={3}>
          <Stack
            direction={'row'}
            justifyContent={'space-between'}
            sx={{ bgcolor: '#' + color, p: 1 }}>
            <Typography color="white">
              {dayjs(
                localDateTimeArrayToDate(exAppt!.info!.startDateTime!)
              ).format('dddd, MMM D')}{' '}
            </Typography>
            {!isConfirmed && (
              <Chip
                key={'customer-feedback-form'}
                label={'Pending'}
                size="small"
                style={{ backgroundColor: 'white', color, width: '5em' }}
              />
            )}
          </Stack>
          <Stack spacing={1} sx={{ p: 1 }}>
            <Typography>
              <Typography variant="subtitle1" component="span">
                Customer:
              </Typography>{' '}
              {customerName}
            </Typography>
            <Typography>
              <Typography variant="subtitle1" component="span">
                Vehicle:
              </Typography>{' '}
              {vehicleYMM}
              {vehicleLicense ? ` (${vehicleLicense})` : ''}
            </Typography>
            <Typography>
              <Typography variant="subtitle1" component="span">
                Concern:
              </Typography>{' '}
              {exAppt.info!.concern}
            </Typography>

            {(exAppt.info!.waiting || exAppt.info!.vip) && (
              <Typography>
                {exAppt.info!.waiting && (
                  <Typography variant="body2" component="span">
                    <SellIcon
                      fontSize="small"
                      sx={{ color: '#' + APPOINTMENT_COLORS_DARK[1] }}
                    />
                    Waiting
                  </Typography>
                )}
                {exAppt.info!.vip && (
                  <Typography variant="body2" component="span">
                    <SellIcon
                      fontSize="small"
                      sx={{ color: '#' + APPOINTMENT_COLORS_DARK[2] }}
                    />
                    VIP
                  </Typography>
                )}
              </Typography>
            )}
            {repairOrder && (
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="subtitle1" component="span">
                  Repair order:
                </Typography>
                <ROLink
                  displayed={'#' + repairOrder.entry!.localId}
                  repairOrderId={repairOrder.entry!.id!}
                  variant="body1"
                />
                {repairOrder.entry!.info!.status ? (
                  <RepairOrderStatusChip
                    statuses={statuses}
                    statusId={repairOrder.entry!.info!.status}
                  />
                ) : (
                  <Typography>Estimate</Typography>
                )}
              </Stack>
            )}
            {technicians && (
              <Typography>
                <Typography variant="subtitle1" component="span">
                  Technicians:
                </Typography>{' '}
                {technicians.map((t) => t.name).join(', ')}
              </Typography>
            )}
            <Stack direction="row" alignItems={'center'}>
              <Typography variant="subtitle1" component="span">
                Color:&nbsp;
              </Typography>
              <AppointmentColorSelect
                color={color}
                setColor={(color) => setColor(color, exAppt)}
              />
            </Stack>
          </Stack>
        </Paper>
      }>
      <Paper
        elevation={elevation}
        sx={{
          ...containerProps,
          bgcolor: exAppt.info!.isConfirmed ? '#' + color : 'white',
          border: exAppt.info!.isConfirmed ? 'none' : `2px solid #${color}`
        }}>
        <Typography
          sx={{
            fontSize: '0.7rem',
            color: exAppt.info!.isConfirmed ? 'white' : `#${color}`
          }}>
          {customerName}
        </Typography>
        {vehicleYMM && (
          <Typography
            sx={{
              fontSize: '0.7rem',
              color: exAppt.info!.isConfirmed ? 'white' : `#${color}`
            }}>
            {vehicleYMM}
          </Typography>
        )}
      </Paper>
    </CalenderTooltip>
  )
}
export const renderEventBlockForNew = (
  calendar: 'appt' | 'resource',
  view: 'week' | 'day',
  color: string,
  customer: CustomerEntry | undefined,
  vehicle: VehicleEntry | undefined
) => {
  const customerName = formatName(customer?.information)

  const vehicleYMM = vehicle ? describeVehicle(vehicle.info!) : ''
  const colorLight = color.slice(0, -2) + '5c'
  return (
    <Paper
      elevation={4}
      sx={{
        ...containerProps,
        bgcolor: '#' + colorLight,
        border: `2px dashed ${'#' + color}`
      }}>
      <Typography sx={{ fontSize: '0.7rem' }} color="black">
        {customerName}
      </Typography>
      {vehicleYMM && (
        <Typography sx={{ fontSize: '0.7rem' }} color="black">
          {vehicleYMM}
        </Typography>
      )}
    </Paper>
  )
}
